import React, { useState } from 'react';
import { Button, Form, Modal } from 'react-bootstrap';
import { IoDownloadOutline } from 'react-icons/io5';
import { FaCloudUploadAlt } from 'react-icons/fa';
import { Spinner } from 'reactstrap';
import { useFormik } from 'formik';
import * as yup from 'yup';
import toast from 'react-hot-toast';
import { uploadBulkAutoNotifications } from '../../api/context/autoNotifications';
import sampleNotificationFile from "../../assets/files/sample-notifications.xlsx";

const BulkNotificationModal = ({ show, onHide, onUpload }) => {
  const [bulkLoading, setBulkLoading] = useState(false);


  const handleExcelFileChange = (event) => {
    const file = event.currentTarget.files[0];
    const supportedFormats = [
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'text/csv'
    ];

    if (file && !supportedFormats.includes(file.type)) {
      toast.error('Unsupported file format. Please upload an Excel or CSV file.');
      return;
    }
    bulkFormik.setFieldValue('excelFile', file);
  };

  const bulkFormik = useFormik({
    initialValues: {
      excelFile: null,
      notificationType: 'bulk' // 'bulk' or 'scheduled'
    },
    validationSchema: yup.object().shape({
      excelFile: yup
        .mixed()
        .required("Excel file is required")
        .test(
          "fileFormat",
          "Unsupported file format",
          (value) => value && [
            'application/vnd.ms-excel',
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            'text/csv'
          ].includes(value.type)
        ),
      notificationType: yup.string().required("Notification type is required")
    }),
    onSubmit: async (values, { resetForm }) => {
      setBulkLoading(true);
      try {
        let formData = new FormData();
        formData.append("file", values.excelFile);
   

        // Call your bulk notification upload API here
        await uploadBulkAutoNotifications(formData)((response) => {
          if (response?.data?.statusCode === 200) {
            setBulkLoading(false);
            onHide();
            resetForm();
            toast.success("Notifications uploaded successfully");
            onUpload(); // Refresh the notifications list
          } else {
            setBulkLoading(false);
            toast.error(response?.data?.message || "Upload failed");
          }
        });

      } catch (error) {
        console.error(error);
        toast.error("Something went wrong! Please try again");
        setBulkLoading(false);
      }
    },
  });

  const downloadSampleExcel = () => {
    const a = document.createElement("a");
    a.href = sampleNotificationFile;
    a.download = `sample-notifications.xlsx`;
    document.body.appendChild(a);
    a.click();
  };


  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      className="modal_create"
      size="md"
    >
      <Modal.Header closeButton className="border-0 pb-20">
        <Modal.Title className="fw-bold">Bulk Notifications Upload</Modal.Title>
      </Modal.Header>
      
      <Modal.Body className="p-30">
        <div className="modal_content_wrapper">

          {/* Excel File Upload */}
          <div className="mb-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6 className="text-dark mb-0">Upload Notifications File</h6>
              <div className="d-flex gap-2">
                <button
                  onClick={downloadSampleExcel}
                  className="text-decoration-none bg-transparent border-0 p-0 d-flex align-items-center gap-1"
                >
                  <IoDownloadOutline className="text-black" size={16} />
                  <span className="text-black" style={{ textDecoration: 'underline', fontSize: '14px' }}>
                    Download Sample
                  </span>
                </button>
             
              </div>
            </div>
            <div className="border rounded p-3 bg-light">
              <Form.Group controlId="formExcelFile">
                <Form.Control
                  type="file"
                  accept=".xlsx, .xls, .csv"
                  onChange={handleExcelFileChange}
                  isInvalid={bulkFormik.touched.excelFile && bulkFormik.errors.excelFile}
                />
                <Form.Control.Feedback type="invalid">
                  {bulkFormik.errors.excelFile}
                </Form.Control.Feedback>
                <Form.Text className="text-muted">
                  Upload Excel or CSV file with notification details. Supported formats: .xlsx, .xls, .csv
                </Form.Text>
              </Form.Group>
            </div>
          </div>


        </div>
      </Modal.Body>
      
      <Modal.Footer className="border-0">
        <Button 
          className='cancel_button' 
          variant="outline-secondary" 
          onClick={onHide}
          disabled={bulkLoading}
        >
          Cancel
        </Button>
        <Button 
          className="request_btn"
          variant="primary"
          onClick={bulkFormik.handleSubmit}
          disabled={!bulkFormik.values.excelFile || bulkLoading}
        >
          {bulkLoading ? (
            <>
              <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
              <span className="ms-2">Uploading...</span>
            </>
          ) : (
            <>
              <FaCloudUploadAlt className="me-2" />
              Upload Notifications
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default BulkNotificationModal;